import type { BookingWithDetails } from "@shared/schema";

function timesOverlap(a: BookingWithDetails, b: BookingWithDetails): boolean {
  const s1 = +new Date(a.startAt);
  const e1 = +new Date(a.endAt);
  const s2 = +new Date(b.startAt);
  const e2 = +new Date(b.endAt);
  return s1 < e2 && s2 < e1;
}

/**
 * Groups bookings that overlap in time (connected components), then assigns a lane index
 * within each group so concurrent events can be laid out side-by-side.
 */
export function assignOverlapLanes(bookings: BookingWithDetails[]): Map<string, { lane: number; maxLanes: number }> {
  const result = new Map<string, { lane: number; maxLanes: number }>();
  if (bookings.length === 0) return result;

  const n = bookings.length;
  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (i: number): number => {
    if (parent[i] !== i) parent[i] = find(parent[i]);
    return parent[i];
  };
  const union = (i: number, j: number) => {
    const pi = find(i);
    const pj = find(j);
    if (pi !== pj) parent[pi] = pj;
  };

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (timesOverlap(bookings[i], bookings[j])) union(i, j);
    }
  }

  const groups = new Map<number, number[]>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r)!.push(i);
  }

  for (const indices of Array.from(groups.values())) {
    const group = indices.map((i: number) => bookings[i]);
    const sorted = [...group].sort((a, b) => +new Date(a.startAt) - +new Date(b.startAt));
    const laneEnds: number[] = [];
    let maxLanes = 0;
    for (const b of sorted) {
      const start = +new Date(b.startAt);
      const end = +new Date(b.endAt);
      let lane = 0;
      for (; lane < laneEnds.length; lane++) {
        if (start >= laneEnds[lane]) break;
      }
      if (lane === laneEnds.length) laneEnds.push(end);
      else laneEnds[lane] = end;
      maxLanes = Math.max(maxLanes, laneEnds.length);
      result.set(b.id, { lane, maxLanes: 0 });
    }
    for (const b of group) {
      const prev = result.get(b.id)!;
      result.set(b.id, { lane: prev.lane, maxLanes });
    }
  }

  return result;
}

/** Pixel gap between side-by-side events in a day column. */
export const CALENDAR_LANE_GAP_PX = 2;

/**
 * Horizontal inset from column edge (matches previous `left: 4` / `right: 4`).
 */
export const CALENDAR_EVENT_INSET_PX = 4;
